import React, { useEffect } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'next-i18next';
import * as EmailValidator from 'email-validator';
import { NAME } from '@app/account/constants';
import { useRouter } from 'next/router';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
    getError,
    getLoading,
    getSucceeded,
    reset,
    resetPassword,
} from '@app/account/models/resetPassword';

// UI
import TextInput from '@ui/components/TextInput';
import ButtonLoadingSpinner from '@ui/components/ButtonLoadingSpinner';
import Link from 'next/link';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';

export interface Props {}

const ResetPasswordForm: React.FC<Props> = ({}) => {
    const { t } = useTranslation(NAME);
    const router = useRouter();
    const redirectUrl = router.query.redirectUrl as string;
    const { query } = router;

    const dispatch = useDispatch();
    const loading = useSelector(getLoading);
    const error = useSelector(getError);
    const succeeded = useSelector(getSucceeded);

    // reset on unmount
    useEffect(
        () => () => {
            dispatch(reset());
        },
        [dispatch],
    );

    const validate = (values: { email: string }): { email?: string } => {
        const errors: { email?: string } = {};

        if (!values.email) {
            errors.email = 'required';
        } else if (!EmailValidator.validate(values.email)) {
            errors.email = 'invalid-email';
        }

        return errors;
    };

    const onSubmit = ({ email }: { email: string }) => {
        dispatch(resetPassword(email));
    };

    return (
        <div className="relative mx-auto mt-12 w-full sm:w-96">
            {succeeded && (
                <div
                    className="left-0 right-0 mx-auto mb-2.5 flex w-fit items-center text-green-500"
                    data-cy="success-message"
                >
                    <CheckCircleIcon className="mr-2 h-16 w-16 text-green-500" />
                    <span className="text-lg font-semibold">{t('reset-password-success')}</span>
                </div>
            )}

            {!succeeded && (
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    render={({ handleSubmit }) => (
                        <form onSubmit={handleSubmit} data-cy="reset-password-form">
                            <div className="rounded-xl border shadow">
                                <Field
                                    name="email"
                                    component={TextInput}
                                    icon="✉️"
                                    placeholder={t('placeholder-email')}
                                />
                            </div>

                            {error && (
                                <div
                                    className="mt-4 flex items-center rounded-xl bg-red-50 p-5 text-sm text-red-500"
                                    data-cy="response-error"
                                >
                                    <span className="ml-2">
                                        {error?.response?.data?.statusCode
                                            ? t(`reset-password-${error.response.data.statusCode}`)
                                            : error?.response?.data?.message}
                                    </span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={succeeded || loading}
                                className="mt-6 w-full rounded-xl bg-blue-500 p-5 text-white transition-colors hover:bg-blue-600"
                            >
                                {loading ? <ButtonLoadingSpinner /> : t('reset-password-btn')}
                            </button>

                            <Link
                                href={{
                                    pathname: redirectUrl || '/login',
                                    query,
                                }}
                                passHref
                                data-cy="login-link"
                            >
                                <p className="mt-12 flex cursor-pointer items-center justify-center text-sm text-gray-500 transition-colors hover:text-blue-500">
                                    <ChevronLeftIcon className="mr-2 h-5 w-5" />
                                    {t('back-to-login')}
                                </p>
                            </Link>
                        </form>
                    )}
                />
            )}

            {succeeded && (
                <Link
                    href={{
                        pathname: redirectUrl || '/login',
                        query,
                    }}
                    passHref
                >
                    <p
                        className="mt-12 flex w-full cursor-pointer items-center justify-center rounded-xl bg-blue-500 p-5 text-white transition-colors hover:bg-blue-600"
                        data-cy="login-link"
                    >
                        {loading ? <ButtonLoadingSpinner /> : t('back-to-login')}
                    </p>
                </Link>
            )}
        </div>
    );
};

export default ResetPasswordForm;
